import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import { useTable, useSortBy, usePagination, useFilters, useRowSelect } from "react-table";
import FilterModal from "../FilterModal/FilterModal";
import DataNavigationModal from "../DataNavigationModal";
import "./DICOMList.css";

const DICOMList = ({ refreshData, onSelectedIdsChange }) => {
  const location = useLocation();
  const token = localStorage.getItem("token");
  const [dicoms, setDicoms] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalResourceId, setModalResourceId] = useState(null);

  const openModalForDicom = (dicom) => {
    setModalResourceId({
      dicomId: dicom.id,
      patientId: dicom.patient,
      studyId: dicom.study,
    });
    setModalOpen(true);
  };

  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = React.useRef();
      const resolvedRef = ref || defaultRef;
      useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);
      return <input type="checkbox" ref={resolvedRef} {...rest} />;
    }
  );

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
        ),
        Cell: ({ row }) => (
          <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
        ),
      },
      {
        Header: "ID",
        accessor: "id",
        filterType: "Exact"
      },
      {
        Header: "Patient",
        accessor: "patient",
        Cell: ({ value }) => <Link to={`/patients/${value}`}>{value}</Link>,
        filterType: "Exact"
      },
      {
        Header: "File Path",
        accessor: "file_path",
        filterType: "Exact"
      },
      {
        Header: "Study ID",
        accessor: "study",
        Cell: ({ value }) => <Link to={`/studies/${value}`}>{value}</Link>,
        filterType: "Exact"
      },
      {
        Header: "Series ID",
        accessor: "series_id",
      },
      {
        Header: "Side",
        accessor: "side",
        filterType: "Exact"
      },
      {
        Header: "Primary Angle",
        accessor: "primary_angle",
      },
      {
        Header: "Secondary Angle",
        accessor: "secondary_angle",
      },
      {
        Header: "Labels",
        accessor: "labels",
        Cell: ({ value }) => (value ? JSON.stringify(value) : "None"),
      },
      {
        Header: "All Images Dir",
        accessor: "all_images_dir",
      },
      {
        Header: "Creation Time",
        accessor: "creation_time",
        Cell: ({ value }) => new Date(value).toLocaleString(),
        filterType: "Exact"
      },
      {
        Header: "Updated Time",
        accessor: "updated_time",
        Cell: ({ value }) => new Date(value).toLocaleString(),
        filterType: "Exact"
      },
      {
        Header: "Uploader",
        accessor: "uploader",
        filterType: "Exact"
      },
      {
        Header: "Actions",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => {
          const dicom = row.original;
          return (
            <div className="actions-container">
              <Link style={{ textDecoration: "none" }} to={`/dicoms/${dicom.id}`}>
                <button className="action-btn view">
                  <i className="fas fa-eye"></i> View
                </button>
              </Link>
              <Link style={{ textDecoration: "none" }} to={`/dicoms/${dicom.id}/edit`}>
                <button className="action-btn edit">
                  <i className="fas fa-edit"></i> Edit
                </button>
              </Link>
              <button
                className="action-btn data"
                onClick={() => openModalForDicom(dicom)}
              >
                <i className="fas fa-database"></i> Data
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({}), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    state,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data: dicoms,
      defaultColumn,
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      pageCount,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const { pageIndex, pageSize, sortBy, filters } = state;

  useEffect(() => {
    onSelectedIdsChange(selectedFlatRows.map(row => row.original.id));
  }, [selectedFlatRows, onSelectedIdsChange]);

  const fetchDICOMs = useCallback(async () => {
    setLoading(true);
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patient");
    const studyId = searchParams.get("study");

    let endpoint = `${process.env.REACT_APP_BACKEND_URL}/patients/dicoms/`;
    if (patientId) {
      endpoint += `?patient=${patientId}`;
    }
    if (studyId) {
      endpoint += patientId ? `&study=${studyId}` : `?study=${studyId}`;
    }

    try {
      let params = {
        page: pageIndex + 1,
        page_size: pageSize,
      };
      if (sortBy?.length > 0) {
        const { id, desc } = sortBy[0];
        params.sort_by = id;
        params.sort_dir = desc ? "desc" : "asc";
      }
      if (filters?.length) {
        filters.forEach(({ id, value }) => {
          if (value) {
            params[id] = value;
          }
        });
      }
      const res = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Token ${token}` },
      });
      const { results, pageCount: serverPageCount } = res.data;
      setDicoms(results || []);
      setPageCount(serverPageCount || 0);
      setPlaceholder(!results || !results.length ? "No DICOMs found." : "");
    } catch (error) {
      console.error(error);
      setPlaceholder("Error fetching DICOMs.");
    }
    setLoading(false);
  }, [pageIndex, pageSize, sortBy, filters, token, location.search]);

  useEffect(() => {
    fetchDICOMs();
  }, [fetchDICOMs, refreshData]);

  if (loading) return <div>Loading DICOMs...</div>;
  if (!page.length && placeholder) {
    return (
      <div className="placeholder-message">
        {placeholder}{" "}
        {filters.length > 0 && (
          <button className="filter-button" onClick={() => setAllFilters([])}>
            Clear Filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="dicom-list-container">
      <div className="table-actions">
        <button className="filter-button" onClick={() => setFilterModalOpen(true)}>
          Filter Data
        </button>
      </div>
      <div className="table-wrapper">
        <table {...getTableProps()} className="dicom-list-table">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colProps } = column.getHeaderProps();
                    return (
                      <th key={colKey} {...colProps}>
                        <div {...(column.getSortByToggleProps ? column.getSortByToggleProps() : {})}>
                          {column.render("Header")}
                          {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key: rowKey, ...rowProps } = row.getRowProps();
              return (
                <tr key={rowKey} {...rowProps}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td key={cellKey} {...cellProps} data-column={cell.column.id}>
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
  
      <div className="pagination-container">
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          Previous
        </button>
        <span>
          Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
        </span>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          Next
        </button>
        <select value={pageSize} style={{ width: "auto" }} onChange={(e) => setPageSize(Number(e.target.value))}>
          {[10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
        {/* Hidden button in case you need to manually trigger selection update */}
        <button onClick={() => {
          const ids = selectedFlatRows.map(row => row.original.id);
          onSelectedIdsChange(ids);
        }} style={{ display: 'none' }}>
          Update Selection
        </button>
      </div>
  
      <DataNavigationModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        contextType="dicom"
        resourceIds={modalResourceId}
      />
  
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        columns={columns.filter((col) => col.filterType && !col.disableFilters)}
        currentFilters={state.filters}
        onApplyFilters={(filters) => {
          setAllFilters(filters);
        }}
      />
    </div>
  );
};

export default DICOMList;
